import {
  PAGE_CONTENT_HEIGHT,
  PAGE_CONTENT_TOP,
  PAGE_FOOTER_MARGIN,
  PAGE_MARGIN_HORIZONTAL,
  PDF_NO_BORDER_LAYOUT,
  TABLE_WIDTH,
} from "./invoicePdfLayout";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { formatInvoicePdfCurrency } from "./formatInvoicePdfLine";
import { buildInvoicePdfLineTreeContent } from "./buildInvoicePdfLineTree";
import { loadHibbertLogoDataUrl } from "./loadHibbertLogoDataUrl";

if (pdfFonts?.pdfMake?.vfs) {
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
}

const HIBBERT_TAGLINE = "A HISTORY OF FORWARD THINKING";

const HIBBERT_ADDRESS_LINES = [
  "400 Pennington Ave.",
  "P.O. Box 8116",
  "Trenton, New Jersey 08650-0116",
  "(609) 394-7500 1-888-HIBBERT",
];

const REMIT_RED = "#CC0000";

const INVOICE_SERVICES_TABLE_LAYOUT = {
  hLineWhenBroken: true,
  hLineWidth: (lineIndex, node) => {
    const bodyLength = node.table.body.length;
    if (lineIndex === 0) {
      return 0.75;
    }
    if (lineIndex === 1) {
      return 0;
    }
    if (lineIndex === 2) {
      return 2;
    }
    if (lineIndex === 3) {
      return 0;
    }
    if (lineIndex === bodyLength - 1) {
      return 0.75;
    }
    if (lineIndex === bodyLength) {
      return 0.75;
    }
    return 0;
  },
  vLineWidth: () => 0.75,
  hLineColor: () => "#000000",
  vLineColor: () => "#000000",
  paddingLeft: (rowIndex, node) => (rowIndex >= 3 || rowIndex === node.table.body.length - 1 ? 10 : 2),
  paddingRight: (rowIndex, node) => (rowIndex >= 3 || rowIndex === node.table.body.length - 1 ? 10 : 2),
  paddingTop: (rowIndex, node) => {
    if (rowIndex === 0) {
      return 0;
    }
    if (rowIndex === 1) {
      return 4;
    }
    if (rowIndex === 2) {
      return 8;
    }
    if (rowIndex === 3) {
      return 8;
    }
    if (rowIndex === node.table.body.length - 1) {
      return 6;
    }
    return 0;
  },
  paddingBottom: (rowIndex, node) => {
    if (rowIndex === 0) {
      return 0;
    }
    if (rowIndex === 1) {
      return 4;
    }
    if (rowIndex === 2) {
      return 8;
    }
    if (rowIndex === 3) {
      return 8;
    }
    if (rowIndex === node.table.body.length - 1) {
      return 6;
    }
    return 0;
  },
};

const REMITTANCE_BOX_LAYOUT = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => REMIT_RED,
  vLineColor: () => REMIT_RED,
  paddingLeft: () => 8,
  paddingRight: () => 8,
  paddingTop: () => 6,
  paddingBottom: () => 6,
};

function uppercaseText(value) {
  return String(value ?? "").trim().toUpperCase();
}

function buildCompanyBlock(logoDataUrl) {
  return {
    stack: [
      logoDataUrl
        ? {
            image: logoDataUrl,
            width: 132,
            margin: [0, 0, 0, 2],
          }
        : { text: "" },
      {
        text: HIBBERT_TAGLINE,
        fontSize: 6.5,
        characterSpacing: 0.8,
        margin: [0, 0, 0, 8],
      },
      ...HIBBERT_ADDRESS_LINES.map((line) => ({
        text: line,
        fontSize: 9.5,
        margin: [0, 0, 0, 1],
      })),
    ],
  };
}

function buildInvoiceSummaryBox(model, showTotalDue) {
  const body = [
    [{ text: "INVOICE", bold: true, alignment: "center", fontSize: 11, margin: [0, 0, 0, 2] }],
    [{ text: `No. ${model.invoiceNumber}`, alignment: "center", fontSize: 10, margin: [0, 0, 0, 2] }],
  ];
  if (showTotalDue) {
    body.push([
      {
        text: `Total Due: ${formatInvoicePdfCurrency(model.totalDue)}`,
        bold: true,
        alignment: "center",
        fontSize: 10,
        margin: [0, 2, 0, 0],
      },
    ]);
  }
  return {
    table: {
      widths: [148],
      body,
    },
    layout: {
      hLineWidth: (lineIndex, node) => {
        if (lineIndex === 0 || lineIndex === node.table.body.length) {
          return 1;
        }
        if (showTotalDue && lineIndex === 2) {
          return 1;
        }
        return 0;
      },
      vLineWidth: () => 1,
      hLineColor: () => "#000000",
      vLineColor: () => "#000000",
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 5,
      paddingBottom: () => 5,
    },
  };
}

function buildBillToBlock(model) {
  const stack = [];
  const contactName = String(model.contactName ?? "").trim();
  if (contactName && contactName !== "—") {
    stack.push({
      text: contactName,
      bold: true,
      fontSize: 10,
      margin: [0, 0, 0, 2],
    });
  }
  const clientName = String(model.clientName ?? "").trim();
  if (clientName) {
    stack.push({
      text: uppercaseText(clientName),
      bold: true,
      fontSize: 10,
      margin: [0, 0, 0, 2],
    });
  }
  for (const line of model.clientAddressLines ?? []) {
    const text = String(line ?? "").trim();
    if (!text) {
      continue;
    }
    stack.push({
      text: uppercaseText(text),
      bold: true,
      fontSize: 10,
      margin: [0, 0, 0, 1],
    });
  }
  return { stack };
}

function buildRemittanceBox(model) {
  const instruction =
    "Please attach a copy of the invoice with your check and remit to:";
  const addressLines = (model.remittanceLines ?? []).slice(3);
  return {
    table: {
      widths: [188],
      body: [
        [
          {
            stack: [
              {
                text: instruction,
                color: REMIT_RED,
                fontSize: 9,
                alignment: "center",
                lineHeight: 1.15,
                margin: [0, 0, 0, 6],
              },
              ...addressLines.map((line, index) => ({
                text: line,
                fontSize: 9,
                alignment: "center",
                lineHeight: 1.15,
                margin: [0, index === 0 ? 0 : 1, 0, 0],
              })),
            ],
          },
        ],
      ],
    },
    layout: REMITTANCE_BOX_LAYOUT,
  };
}

function buildMetadataCell(label, value) {
  return {
    text: [
      { text: `${label}\n`, style: "metaHeader" },
      { text: value, style: "metaValue" },
    ],
    alignment: "center",
  };
}

function buildBodySeparatorLine() {
  return {
    table: {
      widths: ["*"],
      body: [[{ text: "" }]],
    },
    layout: {
      hLineWidth: (lineIndex) => (lineIndex === 1 ? 0.75 : 0),
      vLineWidth: () => 0,
      hLineColor: () => "#000000",
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 8,
    },
    margin: [0, 0, 0, 0],
  };
}

function fullWidthRow(cellContent) {
  return [{ ...cellContent, colSpan: 5 }, {}, {}, {}, {}];
}

function buildInvoiceTotalRow(model) {
  return [
    {
      text: "*FSC® Certificate Code: PBN-COC-004475*",
      italics: true,
      fontSize: 9,
      colSpan: 3,
    },
    {},
    {},
    {
      table: {
        widths: ["auto", "*"],
        body: [
          [
            { text: "Total:", bold: true },
            {
              text: formatInvoicePdfCurrency(model.totalDue),
              bold: true,
              alignment: "right",
              noWrap: true,
            },
          ],
        ],
      },
      layout: PDF_NO_BORDER_LAYOUT,
      colSpan: 2,
    },
    {},
  ];
}

function buildInvoiceServicesTable(model, lineTreeContent, hasLines) {
  const programRow = fullWidthRow({
    stack: [
      {
        text: model.invoiceProgramName,
        bold: true,
        fontSize: 10,
        margin: [0, 0, 0, 2],
      },
      {
        text: model.serviceMonthLabel,
        bold: true,
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },
      buildBodySeparatorLine(),
    ],
  });

  const lineRows = hasLines
    ? lineTreeContent.map((node) => fullWidthRow(node))
    : [
        fullWidthRow({
          text: "No billable lines on this invoice.",
          italics: true,
          margin: [0, 4, 0, 0],
        }),
      ];

  return {
    table: {
      headerRows: 1,
      widths: ["*", "*", "*", "*", "*"],
      body: [
        fullWidthRow({
          text: "\u200B",
          fontSize: 0.5,
          lineHeight: 0.5,
          margin: [0, 0, 0, 0],
        }),
        [
          buildMetadataCell("SALES CODE", model.salesCode),
          buildMetadataCell("PURCHASE ORDER NO.", model.purchaseOrderNumber),
          buildMetadataCell("TERMS", model.terms),
          buildMetadataCell("INVOICE DATE", model.invoiceDate),
          buildMetadataCell("JOB NUMBER", model.jobNumber),
        ],
        fullWidthRow({
          text: "DESCRIPTION OF SERVICES",
          alignment: "center",
          fontSize: 10,
        }),
        programRow,
        ...lineRows,
        buildInvoiceTotalRow(model),
      ],
    },
    width: TABLE_WIDTH,
    layout: INVOICE_SERVICES_TABLE_LAYOUT,
    margin: [0, 8, 0, 8],
  };
}

function buildPageBreakSideBorders() {
  return {
    canvas: [
      {
        type: "line",
        x1: 0,
        y1: 0,
        x2: 0,
        y2: PAGE_CONTENT_HEIGHT,
        lineWidth: 0.75,
        lineColor: "#000000",
      },
      {
        type: "line",
        x1: TABLE_WIDTH,
        y1: 0,
        x2: TABLE_WIDTH,
        y2: PAGE_CONTENT_HEIGHT,
        lineWidth: 0.75,
        lineColor: "#000000",
      },
    ],
    absolutePosition: { x: PAGE_MARGIN_HORIZONTAL, y: PAGE_CONTENT_TOP },
  };
}

function buildPageBreakBottomBorder() {
  return {
    table: {
      widths: [TABLE_WIDTH],
      body: [[{ text: "\u200B", fontSize: 1, lineHeight: 1 }]],
    },
    layout: {
      hLineWidth: (lineIndex) => (lineIndex === 0 ? 0.75 : 0),
      vLineWidth: (lineIndex) => (lineIndex === 0 || lineIndex === 1 ? 0.75 : 0),
      hLineColor: () => "#000000",
      vLineColor: () => "#000000",
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [0, 0, 0, 0],
  };
}

function buildHeaderBlock(model, logoDataUrl, currentPage) {
  const showTotalDue = currentPage === 1;
  return {
    stack: [
      {
        columns: [
          {
            width: "*",
            stack: [buildCompanyBlock(logoDataUrl)],
          },
          {
            width: "auto",
            alignment: "right",
            stack: [buildInvoiceSummaryBox(model, showTotalDue)],
          },
        ],
        columnGap: 12,
      },
      {
        text: "Customer Copy",
        bold: true,
        italics: true,
        alignment: "center",
        fontSize: 11,
        margin: [0, 10, 0, 10],
      },
      {
        columns: [
          {
            width: "*",
            stack: [buildBillToBlock(model)],
            margin: [0, 4, 0, 0],
          },
          {
            width: "auto",
            alignment: "right",
            stack: [buildRemittanceBox(model)],
            margin: [0, 0, 0, 0],
          },
        ],
        columnGap: 16,
      },
    ],
  };
}

function buildServicesSection(model) {
  const lineTreeContent = buildInvoicePdfLineTreeContent(model.lineTree);
  const hasLines =
    lineTreeContent.length > 0 || (model.lineTree?.segments?.length ?? 0) > 0;

  return [buildInvoiceServicesTable(model, lineTreeContent, hasLines)];
}

function buildDocumentDefinition(model, logoDataUrl) {
  const docDefinition = {
    pageSize: "LETTER",
    pageMargins: [PAGE_MARGIN_HORIZONTAL, PAGE_CONTENT_TOP, PAGE_MARGIN_HORIZONTAL, PAGE_FOOTER_MARGIN],
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.15,
      color: "#000000",
    },
    styles: {
      metaHeader: {
        fontSize: 6.5,
      },
      metaValue: {
        bold: true,
        fontSize: 8.5,
      },
    },
    header(currentPage) {
      return {
        margin: [PAGE_MARGIN_HORIZONTAL, 16, PAGE_MARGIN_HORIZONTAL, 0],
        ...buildHeaderBlock(model, logoDataUrl, currentPage),
      };
    },
    footer(currentPage, pageCount) {
      const footerStack = [];
      const isLastPage = currentPage === pageCount;
      const isContinuedPage = currentPage < pageCount;

      if (isContinuedPage) {
        footerStack.push(buildPageBreakSideBorders(), buildPageBreakBottomBorder());
      }

      if (isLastPage) {
        footerStack.push(
          {
            text: "Invoice Distribution:",
            bold: true,
            alignment: "center",
            margin: [0, 0, 0, 2],
          },
          {
            text: model.invoiceDistribution,
            alignment: "center",
            margin: [0, 0, 0, 8],
          }
        );
      }

      footerStack.push({
        text: `Page ${currentPage} of ${pageCount}`,
        bold: true,
        alignment: "center",
        fontSize: 10,
        margin: [0, isContinuedPage ? 16 : footerStack.length ? 4 : 0, 0, 0],
      });

      return {
        margin: [PAGE_MARGIN_HORIZONTAL, 0, PAGE_MARGIN_HORIZONTAL, 18],
        stack: footerStack,
      };
    },
    content: buildServicesSection(model),
    info: {
      title: `Invoice ${model.invoiceNumber}`,
      author: "Hibbert Group",
    },
  };

  if (model.watermark) {
    docDefinition.watermark = model.watermark;
  }

  return docDefinition;
}

export async function openInvoicePdfPreview(model) {
  const logoDataUrl = await loadHibbertLogoDataUrl();
  const docDefinition = buildDocumentDefinition(model, logoDataUrl);
  pdfMake.createPdf(docDefinition).open();
}
